<template>
  <div>
    <div>
      <section-header :ressource="ressource"
                      :isExtended="true"
                      :dialog="dialog"
                      @closeDialog="closeDialog">
      </section-header>
    </div>
    <v-row class="m-0 mx-3" :style="textError">
      <v-col cols="5">
        <v-row>
          Convention collective :
        </v-row>
        <v-row>
          Contexte :
        </v-row>
        <v-row>
          Emploi :
        </v-row>
        <v-row>
          Minimum conventionnel :
        </v-row>
      </v-col>
      <v-col cols="7">
        <v-row>
          {{ convention }}
        </v-row>
        <v-row>
          {{ contexte }}
        </v-row>
        <v-row>
          {{ emploi }}
        </v-row>
        <v-row>
          {{ minimum }}
        </v-row>
      </v-col>
    </v-row>
    <v-row class="m-0">
      <v-col class="col-12 pt-0">
        <v-row class="d-flex">
          <template v-for="(question, index) in graphData.questions">
            <v-col v-if="questionTypeChoix(question)" cols="12" :key="index">
              <v-combobox :label="getLabel(question)"
                          :items="getChoix(question)"
                          :value="getValue(question)"
                          @change="(item: any) => choixChange(question, item)"
                          hideDetails>
              </v-combobox>
            </v-col>
            <v-col v-if="questionTypeEntier(question)" cols="12" :key="index">
              <edit-field :value="getEntier(question)"
                          :editFieldInfo="getFieldInfo(question)"
                          @updated="(value) => entierChange(question, value)">
              </edit-field>
            </v-col>
          </template>
        </v-row>
      </v-col>
    </v-row>
  </div>
</template>

<script lang="ts">
import { EditFieldType, FieldInfo } from '@/framework/base/components/FieldInfo';
import EditField from '@/framework/base/components/EditField.vue';
import SectionHeader from '@/framework/base/components/SectionHeader.vue';
import Graph from '@/generated/classes/Graph';
import Item from '@/framework/base/classes/Item';
import { AppCustoms, FwkButtons, RessourceAppCustom, RessourceFwkButton } from '@/ressources/globalRessources';
import GraphData, { GraphAnswer } from '@/generated/classes/GraphData';
import GraphQuestion, { GraphQuestionTypes } from '@/generated/classes/GraphQuestion';
import { MapRessource } from '@/framework/base/helpers/Ressources';
import { ObjTypes } from '@/generated/classes/ObjTypes';
import { defineComponent, type PropType } from "vue";

export default defineComponent({
  name: "GraphComponent",
  components: {
    SectionHeader,
    EditField
  },
  props: {
    hideTitle: {
      type: Boolean
    },
    dialog: {
      type: Boolean
    },
    title: {
      type: String
    },
    graph: {
      type: Object as PropType<Graph>,
      required: true
    }
  },
  created() {
    this.graphData.graphId = this.graph.id;
    GraphData.process(this.graphData).then((res) => {
      this.graphData = res;
    })
  },
  data() {
    return {
      graphData: new GraphData(),
      applyButton: RessourceFwkButton(FwkButtons.APPLIQUER).label
    };
  },
  computed: {
    ressource() {
      return RessourceAppCustom(AppCustoms.TEST_GRAPH);
    },
    convention(): string {
      let res = '';
      if (this.graph) {
        res = this.graph.convention;
      }
      return this.emptyRow(res);
    },
    contexte(): string {
      return this.emptyRow(this.graphData.contexte);
    },
    emploi(): string {
      return this.emptyRow(this.graphData.metier);
    },
    minimum(): string {
      let res = '';
      if (this.graphData.minimum) {
        res = this.graphData.minimum.montant.toFixed(2) + ' € (' + this.graphData.minimum.mode + ')';
      }
      return this.emptyRow(res);
    },
    resultat(): string {
      return 'to do';
    },
    textError(): string {
      if (this.graphData.error) {
        return 'color:red';
      } else {
        return '';
      }
    }
  },
  methods: {
    questionTypeChoix(question: GraphQuestion): boolean {
      return (question.type == GraphQuestionTypes.CHOIX ||
        question.type == GraphQuestionTypes.BOOL) &&
        question.choix.length > 1;
    },
    questionTypeEntier(question: GraphQuestion): boolean {
      return question.type == GraphQuestionTypes.ENTIER;
    },
    getLabel(question: GraphQuestion): string {
      let res = '';
      res = ObjTypes[question.objType];
      if (question.calcul) {
        res += ' (calculée)';
      }
      res += ' : ' + question.label;
      return res;
    },
    getChoix(question: GraphQuestion): Item[] {
      const res: Item[] = [];
      question.choix.forEach((choix, index) => {
        res.push(new Item(index, choix.label));
      });
      if (question.sortChoix) {
        res.sort((a, b) => a.title > b.title ? 1 : -1);
      }
      return res;
    },
    getValue(question: GraphQuestion): string {
      let res = '';
      if (question.answer) {
        res = question.answer.label;
      }
      return res;
    },
    getEntier(question: GraphQuestion): string {
      let res = '';
      if (question.answer) {
        res = question.answer.value;
      }
      return res;
    },
    mask(value: string): string {
      return '';
    },
    getFieldInfo(question: GraphQuestion): FieldInfo {
      const ressource = new MapRessource();
      ressource.label = this.getLabel(question);
      const type = EditFieldType.INT;
      const precision = 2;
      const format = null;
      return new FieldInfo(ressource, type, precision, this.mask, format, null, null);
    },
    emptyRow(value: string): string {
      if (value == '') {
        return '-';
      } else {
        return value;
      }
    },
    choixChange(question: GraphQuestion, item: Item) {
      this.clearQuestions(question);
      const value = question.choix.find(a => a.label == item.title);
      if (value) {
        question.answer = new GraphAnswer();
        question.answer.donnee = question.donnee;
        question.answer.value = value.value;
        question.answer.label = value.label;
        GraphData.process(this.graphData).then((res) => {
          this.graphData = res;
        })
      }
    },
    entierChange(question: GraphQuestion, value: string) {
      this.clearQuestions(question);
      question.answer = new GraphAnswer();
      question.answer.donnee = question.donnee;
      question.answer.value = value;
      GraphData.process(this.graphData).then((res) => {
        this.graphData = res;
      })
    },
    clearQuestions(question: GraphQuestion) {
      const index = this.graphData.questions.indexOf(question);
      if (index >= 0) {
        for (let i = this.graphData.questions.length - 1; i > index; i--) {
          this.graphData.questions.splice(i);
        }
      }
    },
    closeDialog() {
      this.$emit('closeDialog');
    }
  }
})

</script>
